import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useChatHistory } from '../hooks/useChatHistory';
import { ChatMessage, Chat, EnabledPaths, UsageInfo, Dataset } from '../types';
import {
  getSession,
  getSettings,
  getUsage,
  listAllDatasets,
  selectDatasets,
  sendQuery,
  SESSION_ID_STORAGE_KEY,
  updatePaths,
  updateShowAds,
  updateSubscription,
  assignLicense as assignLicenseApi,
  removeLicense as removeLicenseApi,
  saveOrganizationDetails,
  registerGoogle,
  SESSION_TOKEN_KEY,
  suggestTitle,
  getChatMessages,
  pinChat as pinChatApi,
  deleteChat as deleteChatApi,
} from '../services/api';
import { v4 as uuidv4 } from 'uuid';
import { ensureDeviceToken } from '../utils/deviceToken';
import { useStripe } from '@stripe/react-stripe-js';
import { CredentialResponse } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { ThinkingStep, ThoughtStep } from '../types/index';
import { trackEvent, AnalyticsEvents } from '../utils/analytics';

const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

type ChatWindow = {
  chatId: string;
  total: number;
  start: number; // index-like window bookkeeping for UI only
  end: number;   // "
  messages: ChatMessage[]; // visible window (tail + any older pages prepended)
};

interface ChatContextType {
  chats: Chat[];
  activeChat: Chat | undefined;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  setActiveChatId: (id: string | null) => Promise<void>;
  activeChatId: string | null;
  createNewChat: () => string;
  sendMessage: (messageText: string, thoughtLimit: number, files?: File[]) => Promise<void>;
  isSettingsOpen: boolean;
  settingsTab:
    | 'settings'
    | 'account'
    | 'licenses'
    | 'knowledgebase'
    | 'memories'
    | 'notes'
    | 'personality';
  toggleSettingsModal: (
    open: boolean,
    tab?:
      | 'settings'
      | 'account'
      | 'licenses'
      | 'knowledgebase'
      | 'memories'
      | 'notes'
      | 'personality'
  ) => void;
  enabledPaths: EnabledPaths;
  updateEnabledPaths: (paths: EnabledPaths) => void;
  activeDatasets: string[];
  allDatasets: string[];
  updateActiveDatasets: (datasets: string[]) => void;
  usageInfo: UsageInfo | null;
  updateSubscriptionTier: (tier: string) => Promise<void>;
  toggleShowAds: (show: boolean) => Promise<void>;
  isAdmin: boolean;
  licensedUsers: string[];
  licenseQuota: number;
  assignLicense: (email: string) => Promise<void>;
  removeLicense: (email: string) => Promise<void>;
  loadMoreChats: () => Promise<void>;
  hasMoreChats: boolean;
  userId: string | null;
  setSessionId: (id: string | null) => void;
  setToken: (id: string | null) => void;
  fetchInitialData: () => void;
  isEnterpriseModalOpen: boolean;
  openEnterpriseModal: () => void;
  closeEnterpriseModal: () => void;
  saveEnterpriseDetails: (quantity: number, organizationName: string) => Promise<void>;
  error: string;
  token: string | null;
  updateThinkingSteps: (steps: React.SetStateAction<ThinkingStep[]>) => void;
  thinkingSteps: ThinkingStep[];
  updateThoughtSteps: (steps: ThinkingStep[]) => void;
  thoughtSteps: ThoughtStep[];
  chatSocketId: string;
  pinnedChats: string[];
  pinChat: (chatId: string) => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;

  getVisibleMessages: (chatId?: string | null) => ChatMessage[];
  loadOlderForActive: () => Promise<void>;
  setMessages: (chatId: string, messages: ChatMessage[]) => void; // kept for API parity; now a hard replace
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);
export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within a ChatProvider');
  return ctx;
};

interface ChatProviderProps { children: ReactNode; }

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setSessionIdState] = useState<string | null>(localStorage.getItem(SESSION_ID_STORAGE_KEY));
  const [token, setTokenState] = useState<string | null>(localStorage.getItem(SESSION_TOKEN_KEY));
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<
    'settings' | 'account' | 'licenses' | 'knowledgebase' | 'memories' | 'notes' | 'personality'
  >('settings');
  const [enabledPaths, setEnabledPaths] = useState<EnabledPaths>({ Si: true, Ne: true, Ni: true });
  const [activeDatasets, setActiveDatasets] = useState<string[]>([]);
  const [chatSocketId, setChatSocketId] = useState<string>('');
  const [pinnedChats, setPinnedChats] = useState<string[]>([]);
  const [allDatasets, setAllDatasets] = useState<string[]>([]);
  const [usageInfo, setUsageInfo] = useState<UsageInfo | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [licensedUsers, setLicensedUsers] = useState<string[]>([]);
  const [licenseQuota, setLicenseQuota] = useState<number>(0);
  const [lastFetchDate, setLastFetchDate] = useState<number>(Date.now());
  const [isEnterpriseModalOpen, setEnterpriseModalOpen] = useState(false);
  const openEnterpriseModal = () => setEnterpriseModalOpen(true);
  const closeEnterpriseModal = () => setEnterpriseModalOpen(false);
  const [error, setError] = useState<string>('');
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
  const [thoughtSteps, setThoughtSteps] = useState<ThoughtStep[]>([]);

  // SINGLE SOURCE OF TRUTH for messages (no merging)
  const [windowsByChatId, setWindowsByChatId] = useState<Record<string, ChatWindow>>({});
  const windowsRef = useRef(windowsByChatId);
  useEffect(() => { windowsRef.current = windowsByChatId; }, [windowsByChatId]);

  // Prevent /messages spamming: per-chat in-flight lock + debounced scroll
  const inflightRef = useRef<Record<string, boolean>>({});
  const scrollDebounceRef = useRef<Record<string, number>>({}); // store setTimeout ids

  const stripe = useStripe();

  const setSessionId = (id: string | null) => {
    setSessionIdState(id);
    if (id) localStorage.setItem(SESSION_ID_STORAGE_KEY, id);
    else localStorage.removeItem(SESSION_ID_STORAGE_KEY);
  };
  const setToken = (id: string | null) => {
    setTokenState(id);
    if (id) localStorage.setItem(SESSION_TOKEN_KEY, id);
    else localStorage.removeItem(SESSION_TOKEN_KEY);
  };

  const {
    chats,
    activeChatId,
    setActiveChatId: setActiveChatIdHistory,
    createNewChat: createHistoryChat,
    addMessageToChat, // unused
    updateChatTitle,
    getActiveChat,
    loadMoreChats,
    hasMore,
    removeChat,
  } = useChatHistory(userId || undefined);

  // bootstrapping
  useEffect(() => {
    let storedSession = localStorage.getItem(SESSION_ID_STORAGE_KEY);
    let storedToken = localStorage.getItem(SESSION_TOKEN_KEY);

    const fetchInitialData = async () => {
      if (!storedSession) {
        const { user_id, token } = await getSession(storedSession || undefined, storedToken || undefined);
        setSessionId(user_id);
        setToken(token);
        storedSession = user_id;
      }
      if (storedSession) {
        try {
          const allData = await listAllDatasets(storedSession, true);
          setAllDatasets(allData.datasets.map((d: Dataset) => d.name));
        } catch (e) { console.error('datasets', e); }
        try {
          const [settings, usage] = await Promise.all([
            getSettings(storedSession, setSessionId, setToken),
            getUsage(storedSession, setSessionId, setToken),
          ]);
          setPinnedChats(settings.pinned_chats || []);
          setChatSocketId(settings.pinned_chats?.[0] || '');
          setEnabledPaths(settings.enabled_paths);
          setActiveDatasets(settings.active_datasets);
          setIsAdmin(settings.admin);
          setLicensedUsers(settings.licensed_users || []);
          setLicenseQuota(settings.license_quota || 0);
          setUsageInfo(usage);
        } catch (e) { console.error('session data', e); }
      }
    };
    fetchInitialData();
  }, [lastFetchDate]);

  useEffect(() => {
    const fetchSessionData = async () => {
      if (!userId) return;
      try {
        const [settings, usage, allData] = await Promise.all([
          getSettings(userId, setSessionId, setToken),
          getUsage(userId, setSessionId, setToken),
          listAllDatasets(userId, true, setSessionId, setToken),
        ]);
        setPinnedChats(settings.pinned_chats || []);
        setChatSocketId(settings.pinned_chats?.[0] || '');
        setEnabledPaths(settings.enabled_paths);
        setActiveDatasets(settings.active_datasets);
        setIsAdmin(settings.admin);
        setLicensedUsers(settings.licensed_users || []);
        setLicenseQuota(settings.license_quota || 0);
        setUsageInfo(usage);
        setAllDatasets(allData.datasets.map((d: Dataset) => d.name));
      } catch (e) { console.error('fetch session', e); }
    };
    fetchSessionData();
  }, [userId]);

  const toggleSettingsModal = (
    open: boolean,
    tab: ChatContextType['settingsTab'] = 'settings'
  ) => {
    setSettingsOpen(open);
    setSettingsTab(tab);
    trackEvent(open ? AnalyticsEvents.SettingsOpened : AnalyticsEvents.SettingsClosed, { tab });
  };

  const updateEnabledPaths = async (newPaths: EnabledPaths) => {
    setEnabledPaths(newPaths);
    trackEvent(AnalyticsEvents.PathsUpdated, { paths: newPaths });
    if (userId) {
      try { await updatePaths(userId, { enabled_paths: newPaths }, setSessionId, setToken); }
      catch (e) { console.error('update paths', e); setEnabledPaths(enabledPaths); }
    }
  };

  const updateActiveDatasets = async (newDatasets: string[]) => {
    setActiveDatasets(newDatasets);
    trackEvent(AnalyticsEvents.DatasetSelected, { datasets: newDatasets });
    if (userId) {
      try { await selectDatasets(userId, { active_datasets: newDatasets }, setSessionId, setToken); }
      catch (e) { console.error('update datasets', e); setActiveDatasets(activeDatasets); }
    }
  };

  const assignLicense = async (email: string) => {
    if (!userId) return;
    try { const res = await assignLicenseApi(userId, email, setSessionId, setToken); setLicensedUsers(res.licensed_users); }
    catch (e) { console.error('assign license', e); }
  };

  const pinChat = async (chatId: string) => {
    if (!userId) return;
    try {
      const res = await pinChatApi(userId, chatId, !pinnedChats.includes(chatId), setSessionId, setToken);
      setPinnedChats(res.pinned_chats || []);
      setChatSocketId(res.pinned_chats?.[0] || chatSocketId);
    } catch (e) { console.error('pin chat', e); }
  };

  const deleteChat = async (chatId: string) => {
    if (!userId) return;
    try {
      await deleteChatApi(userId, chatId, setSessionId, setToken);
      removeChat(chatId);
      setPinnedChats(prev => prev.filter(id => id !== chatId));
      if (activeChatId === chatId) setActiveChatId(null);
      setWindowsByChatId(prev => {
        const next = { ...prev }; delete next[chatId]; return next;
      });
    } catch (e) { console.error('delete chat', e); }
  };

  const removeLicense = async (email: string) => {
    if (!userId) return;
    try { const res = await removeLicenseApi(userId, email, setSessionId, setToken); setLicensedUsers(res.licensed_users); }
    catch (e) { console.error('remove license', e); }
  };

  const handleGoogleSuccess = (res: CredentialResponse) => {
    if (!res.credential) return;
    try {
      const decoded: { sub: string; email?: string } = jwtDecode(res.credential);
      setSessionId(decoded.sub);
      if (decoded.email) {
        registerGoogle(decoded.sub, decoded.email, res.credential, setSessionId, setToken);
      }
    } catch (e) { console.error('decode credential', e); }
  };

  const saveEnterpriseDetails = async (quantity: number, organizationName: string) => {
    if (!userId) return;
    try {
      await saveOrganizationDetails(userId, organizationName, quantity, setSessionId, setToken);
      setLicenseQuota(quantity);
      await updateSubscriptionTier('Enterprise');
    } catch (e) { console.error('save enterprise', e); }
  };

  const finalizeTitle = (chatId: string) => {
    const chat = getActiveChat();
    if (!chatId) return;
    if (chat && new Date(chat?.updated_at) > new Date(Date.now() + 900)) return;
    try {
      suggestTitle(chatId, 16, userId || undefined, setSessionId, setToken)
        .then((res: any) => { if (res?.title) updateChatTitle(chatId, res.title); });
    } catch (e) { console.error('finalize title', e); }
  };

  const setActiveChatId = async (id: string | null) => {
    if (id === activeChatId) return;
    if (id) finalizeTitle(id);
    setActiveChatIdHistory(id);
    if (id) {
      trackEvent(AnalyticsEvents.ChatOpened, { chatId: id });
      void openChatWindow(id, 50); // authoritative tail hydrate, small N
    }
  };

  const createNewChat = (): string => {
    const newChat = createHistoryChat();
    setActiveChatId(newChat.id);
    trackEvent(AnalyticsEvents.NewChatCreated, { chatId: newChat.id });
    return newChat.id;
  };

  const dedupeByKey = (msgs: ChatMessage[]): ChatMessage[] => {
    const seen = new Set<string>();
    const res: ChatMessage[] = [];
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i];
      const key = m.client_message_id ?? m.id;
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      res.push(m);
    }
    return res.reverse();
  };

  // Hard replace of full message list for a chat
  const setMessages = (chatId: string, messageList: ChatMessage[]) => {
    setWindowsByChatId(prev => {
      const curr = prev[chatId] ?? { chatId, total: 0, start: 0, end: 0, messages: [] as ChatMessage[] };
      const deduped = dedupeByKey(messageList);
      return { ...prev, [chatId]: { ...curr, messages: deduped, total: Math.max(curr.total, deduped.length) } };
    });
  };

  // Replace last k messages (authoritative server tail)
  const replaceTail = (chatId: string, serverMsgs: ChatMessage[], total?: number) => {
    setWindowsByChatId(prev => {
      const curr = prev[chatId] ?? { chatId, total: 0, start: 0, end: 0, messages: [] as ChatMessage[] };
      const k = serverMsgs.length;
      const base = curr.messages.slice(0, Math.max(0, curr.messages.length - k));
      const nextMessages = dedupeByKey(base.concat(serverMsgs));
      return {
        ...prev,
        [chatId]: {
          ...curr,
          messages: nextMessages,
          total: total ?? Math.max(curr.total, nextMessages.length),
        },
      };
    });
  };

  // Prepend older page (assumes server returns a strict prefix older than current window)
  const prependOlder = (chatId: string, olderMsgs: ChatMessage[], newStart: number, total?: number) => {
    setWindowsByChatId(prev => {
      const curr = prev[chatId] ?? { chatId, total: 0, start: 0, end: 0, messages: [] as ChatMessage[] };
      const nextMessages = dedupeByKey(olderMsgs.concat(curr.messages));
      return {
        ...prev,
        [chatId]: {
          ...curr,
          messages: nextMessages,
          start: newStart,
          total: total ?? Math.max(curr.total, nextMessages.length),
        },
      };
    });
  };

  const getVisibleMessages = (chatId?: string | null): ChatMessage[] => {
    const id = chatId ?? activeChatId;
    if (!id) return [];
    return windowsByChatId[id]?.messages ?? [];
  };

  // -------------- network: tail & windows (with in-flight locks) --------------
  const withInFlightLock = async (chatId: string, fn: () => Promise<void>) => {
    if (inflightRef.current[chatId]) return;
    inflightRef.current[chatId] = true;
    try { await fn(); }
    finally { inflightRef.current[chatId] = false; }
  };

  const fetchTail = async (chatId: string, windowSize = 50) => {
    if (!userId || !token) return;
    const start = Number.MAX_SAFE_INTEGER - windowSize;
    const end   = Number.MAX_SAFE_INTEGER;
    await withInFlightLock(chatId, async () => {
      const data = await getChatMessages(chatId, userId, token, start, end);
      replaceTail(chatId, data.messages ?? [], data.total);
      // For UI bookkeeping:
      setWindowsByChatId(prev => ({
        ...prev,
        [chatId]: { ...(prev[chatId] ?? { chatId, total: 0, start: 0, end: 0, messages: [] }),
          start: data.start ?? start, end: data.end ?? end }
      }));
    });
  };

  const loadOlderForActive = async (pageSize = 50) => {
    if (!activeChatId || !userId || !token) return;

    // Debounce per chatId to avoid spam on rapid scroll events
    const key = activeChatId;
    if (scrollDebounceRef.current[key]) {
      window.clearTimeout(scrollDebounceRef.current[key]);
    }
    await new Promise<void>(resolve => {
      scrollDebounceRef.current[key] = window.setTimeout(async () => {
        const win = windowsRef.current[key];
        if (!win || win.start <= 0) { resolve(); return; }
        const nextStart = Math.max(0, win.start - pageSize);
        const nextEnd = win.start;
        await withInFlightLock(key, async () => {
          const data = await getChatMessages(key, userId, token, nextStart, nextEnd);
          // assume server returns strictly older block
          prependOlder(key, data.messages ?? [], data.start ?? nextStart, data.total);
        });
        resolve();
      }, 250); // 250ms debounce
    });
  };

  const openChatWindow = async (chatId: string, pageSize = 50) => {
    await fetchTail(chatId, pageSize); // single authoritative fetch; no merge, no spam
  };

  // thinking
  const updateThoughtSteps = (newSteps: ThinkingStep[]) => {
    setThoughtSteps(newSteps.map(step => ({
      agent: step.agent, text: step.output, timestamp: Date.now(),
    })) as ThoughtStep[]);
  };
  const updateThinkingSteps = (newSteps: React.SetStateAction<ThinkingStep[]>) => {
    setThinkingSteps(newSteps);
  };

  // send message (optimistic user; final tail refresh authoritative)
  const sendMessage = async (messageText: string, thoughtLimit: number, files: File[] = []) => {
    let currentChat = getActiveChat() || undefined;
    if (!currentChat) {
      const created = createHistoryChat();
      await setActiveChatId(created.id);
      currentChat = { ...created };
    }
    const chatId = currentChat.id;

    const attachments: ChatMessage['attachments'] = files.length
      ? await Promise.all(files.map(async (f) => ({ name: f.name, type: f.type, dataUrl: await readFileAsDataURL(f) })))
      : undefined;

    const deviceId = await ensureDeviceToken();
    const clientMessageId = deviceId + ':' + (crypto?.randomUUID?.() ?? uuidv4());

    const userMessage: ChatMessage = {
      id: clientMessageId,
      client_message_id: clientMessageId,
      sender: 'user',
      text: messageText,
      attachments,
    };

    // Append locally (no merge)
    setWindowsByChatId(prev => {
      const curr = prev[chatId] ?? { chatId, total: 0, start: 0, end: Number.MAX_SAFE_INTEGER, messages: [] as ChatMessage[] };
      const nextMessages = curr.messages.concat(userMessage);
      return { ...prev, [chatId]: { ...curr, messages: nextMessages, total: Math.max(curr.total, nextMessages.length) } };
    });

    trackEvent(AnalyticsEvents.ChatMessageSent, { chatId, hasFiles: files.length > 0 });
    setIsLoading(true);

    try {
      const queryResult = await sendQuery(messageText, thoughtLimit, files, userId, chatId, clientMessageId, setSessionId, setToken);

      // Render a provisional assistant bubble (optional) — gets replaced by tail refresh anyway
      const provisionalAssistant: ChatMessage = {
        id: uuidv4(),
        sender: 'enantio',
        text: String((queryResult as any)?.response ?? (queryResult as any)?.message?.text ?? ''),
      };
      setWindowsByChatId(prev => {
        const curr = prev[chatId] ?? { chatId, total: 0, start: 0, end: Number.MAX_SAFE_INTEGER, messages: [] as ChatMessage[] };
        const nextMessages = curr.messages.concat(provisionalAssistant);
        return { ...prev, [chatId]: { ...curr, messages: nextMessages, total: Math.max(curr.total, nextMessages.length) } };
      });

      // Authoritative tail refresh (replaces the last N, removing TEMP & provisional copies)
      await fetchTail(chatId, 50);

      const usage = await getUsage(userId || (queryResult as any)?.userId, setSessionId, setToken);
      setUsageInfo(usage);
    } catch (err: any) {
      console.error('AI response', err);
      const isLimit = err instanceof Error && (err as any).status === 402;
      const errorMessage: ChatMessage = isLimit
        ? (usageInfo?.tier === 'Free' && !usageInfo?.showAds
            ? { id: uuidv4(), sender: 'enantio', text: 'You have run out of thoughts. Click here to enable ads for 25 more.', adsPrompt: true }
            : { id: uuidv4(), sender: 'enantio', text: 'You have run out of thoughts. Click here to upgrade.', upgradePrompt: true })
        : { id: uuidv4(), sender: 'enantio', text: `Sorry, something went wrong. (Details: ${err instanceof Error ? err.message : 'Unknown error'})` };

      setWindowsByChatId(prev => {
        const curr = prev[chatId] ?? { chatId, total: 0, start: 0, end: Number.MAX_SAFE_INTEGER, messages: [] as ChatMessage[] };
        const nextMessages = curr.messages.concat(errorMessage);
        return { ...prev, [chatId]: { ...curr, messages: nextMessages, total: Math.max(curr.total, nextMessages.length) } };
      });
      const usage = await getUsage(userId || '', setSessionId, setToken);
      setUsageInfo(usage);
    } finally {
      setIsLoading(false);
    }
  };

  const updateSubscriptionTier = async (tier: string) => {
    if (!userId) return;
    try {
      const response = await updateSubscription(userId, tier, setSessionId, setToken);
      if (response.sessionId) {
        if (!stripe) { setError('Stripe failed to load. Please refresh and try again.'); return; }
        const { error } = await stripe.redirectToCheckout({ sessionId: response.sessionId });
        if (error) { setError('Stripe redirect failed: ' + error.message); console.error('Stripe redirect error:', error); }
        return;
      }
      const [usage, settings] = await Promise.all([
        getUsage(userId, setSessionId, setToken),
        getSettings(userId, setSessionId, setToken),
      ]);
      setUsageInfo(usage);
      setIsAdmin(settings.admin);
      setLicensedUsers(settings.licensed_users || []);
      toggleSettingsModal(false);
    } catch (e) {
      setError('Failed to update subscription. Please try again.');
      console.error('update subscription', e);
    }
  };

  const toggleShowAds = async (show: boolean) => {
    if (!userId) return;
    try {
      await updateShowAds(userId, show, setSessionId, setToken);
      const usage = await getUsage(userId, setSessionId, setToken);
      setUsageInfo(usage);
    } catch (e) {
      console.error('toggle show ads', e);
    }
  };

  const derivedActiveChat = useMemo(
    () => (activeChatId ? chats.find(c => c.id === activeChatId) : undefined),
    [chats, activeChatId]
  );

  const chatsWithoutMessages = useMemo<Chat[]>(
    () => chats.map(c => ({ ...c, messages: [] })),
    [chats]
  );
  const activeChatWithoutMessages = useMemo<Chat | undefined>(
    () => (derivedActiveChat ? { ...derivedActiveChat, messages: [] } : undefined),
    [derivedActiveChat]
  );

  const value: ChatContextType = {
    chats: chatsWithoutMessages,
    activeChat: activeChatWithoutMessages,

    isLoading,
    setIsLoading: (loading: boolean) => setIsLoading(loading),

    setActiveChatId,
    activeChatId,
    createNewChat,
    sendMessage,

    isSettingsOpen,
    settingsTab,
    toggleSettingsModal,
    enabledPaths,
    updateEnabledPaths,
    activeDatasets,
    allDatasets,
    updateActiveDatasets,
    usageInfo,
    updateSubscriptionTier,
    toggleShowAds,
    isAdmin,
    licensedUsers,
    licenseQuota,
    assignLicense,
    removeLicense,
    loadMoreChats,
    hasMoreChats: hasMore,
    userId,
    setSessionId,
    setToken,
    fetchInitialData: () => setLastFetchDate(Date.now()),
    isEnterpriseModalOpen,
    openEnterpriseModal,
    closeEnterpriseModal,
    saveEnterpriseDetails,
    error,
    token,
    updateThinkingSteps,
    thinkingSteps,
    updateThoughtSteps,
    thoughtSteps,
    chatSocketId,
    pinnedChats,
    pinChat,
    deleteChat,

    getVisibleMessages: (chatId?: string | null) => getVisibleMessages(chatId),

    // Debounced, lock-guarded
    loadOlderForActive,

    // Hard replace (kept for API parity where needed)
    setMessages,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
